import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import api, { formatMoney } from "@/lib/api";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function AdminFinanciero() {
    const [year, setYear] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);

    const load = useCallback(async (y) => {
        try { const { data: d } = await api.get(`/admin/finance?year=${y}`); setData(d); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(year); }, [year, load]);

    if (!data) return <div className="overline text-muted-foreground">Cargando…</div>;
    const t = data.totals;
    const maxBar = Math.max(1, ...data.monthly.map((m) => m.fixed + m.projected));

    return (
        <div className="space-y-4" data-testid="admin-financiero">
            <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => setYear(year - 1)} className="rounded-none" data-testid="fin-prev-year">←</Button>
                <span className="font-display text-3xl tabular" data-testid="fin-year">{data.year}</span>
                <Button variant="outline" size="sm" onClick={() => setYear(year + 1)} className="rounded-none" data-testid="fin-next-year">→</Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label="Ingresos fijos (ventas)" value={formatMoney(t.fixed)} sub={`${t.confirmed_count} reservas efectivas`} testid="fin-fixed" accent="text-eucalyptus" border="border-eucalyptus" />
                <Kpi label="Proyectado (tentativas)" value={formatMoney(t.projected)} sub={`${t.tentative_count} cotizaciones activas`} testid="fin-projected" accent="text-accent" border="border-accent" />
                <Kpi label="Pipeline total" value={formatMoney(t.pipeline_total)} testid="fin-pipeline" />
                <Kpi label="Cobrado (abonos)" value={formatMoney(t.received)} testid="fin-received" />
                <Kpi label="Por cobrar (fijo)" value={formatMoney(t.pending_collect)} testid="fin-pending" />
            </div>

            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-4">Proyección mensual {data.year} <span className="text-xs text-muted-foreground font-sans">(por fecha del evento)</span></h3>
                <div className="space-y-2" data-testid="fin-monthly-bars">
                    {data.monthly.map((m) => (
                        <div key={m.month} className="grid grid-cols-[40px_1fr_260px] gap-3 items-center text-xs">
                            <span className="font-display text-sm">{MONTHS[m.month - 1]}</span>
                            <div className="h-5 bg-secondary flex overflow-hidden">
                                <div className="h-full bg-eucalyptus" style={{ width: `${(m.fixed / maxBar) * 100}%` }} title={`Fijo ${formatMoney(m.fixed)}`} />
                                <div className="h-full bg-accent/70" style={{ width: `${(m.projected / maxBar) * 100}%` }} title={`Proyectado ${formatMoney(m.projected)}`} />
                            </div>
                            <span className="tabular text-right">
                                <span className="text-eucalyptus">{formatMoney(m.fixed)}</span>
                                <span className="text-muted-foreground"> + </span>
                                <span className="text-accent">{formatMoney(m.projected)}</span>
                                <span className="text-muted-foreground"> · cobrado {formatMoney(m.received)}</span>
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex gap-4 mt-4 text-[10px] overline text-muted-foreground">
                    <span><span className="inline-block h-3 w-3 bg-eucalyptus mr-1 align-middle" /> Fijo (ventas confirmadas)</span>
                    <span><span className="inline-block h-3 w-3 bg-accent/70 mr-1 align-middle" /> Proyectado (reservas tentativas)</span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ListCard title="Reservas efectivas (ventas)" items={data.confirmed} testid="fin-confirmed-list" color="text-eucalyptus" />
                <ListCard title="Reservas tentativas (cotizaciones)" items={data.tentative} testid="fin-tentative-list" color="text-accent" />
            </div>

            <div className="bg-white border border-border p-6" data-testid="fin-pending-events">
                <h3 className="font-display text-2xl mb-1 text-destructive">Eventos con saldo pendiente</h3>
                <p className="text-xs text-muted-foreground mb-4">Ventas confirmadas cuyo total aún no está cubierto por los abonos registrados — para seguimiento de cobro.</p>
                {(data.pending_events || []).length === 0 ? (
                    <div className="overline text-muted-foreground">Sin saldos pendientes este año 🎉</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="fin-pending-table">
                            <thead className="bg-secondary">
                                <tr>{["Evento / cliente", "Fecha", "Valor total", "Cobrado", "Pendiente"].map((h) => (
                                    <th key={h} className="text-left px-3 py-2 overline">{h}</th>
                                ))}</tr>
                            </thead>
                            <tbody>
                                {data.pending_events.map((e) => (
                                    <tr key={e.id} className="border-t border-border">
                                        <td className="px-3 py-2 font-display text-sm">{e.name}</td>
                                        <td className="px-3 py-2 tabular">{e.date || "sin fecha"}</td>
                                        <td className="px-3 py-2 tabular">{formatMoney(e.total)}</td>
                                        <td className="px-3 py-2 tabular text-eucalyptus">{formatMoney(e.received)}</td>
                                        <td className="px-3 py-2 tabular text-destructive font-medium">{formatMoney(e.pending)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value, sub, testid, accent = "", border = "border-border" }) {
    return (
        <div className={`bg-white border p-4 ${border}`} data-testid={testid}>
            <div className="overline text-muted-foreground text-[10px]">{label}</div>
            <div className={`font-display text-2xl md:text-3xl tabular mt-1 ${accent}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}

function ListCard({ title, items, testid, color }) {
    return (
        <div className="bg-white border border-border p-5" data-testid={testid}>
            <h4 className={`font-display text-xl mb-3 ${color}`}>{title}</h4>
            <div className="space-y-1.5 text-xs max-h-64 overflow-y-auto">
                {items.map((i) => (
                    <div key={i.id} className="flex justify-between border-b border-border pb-1.5">
                        <span>{i.name} <span className="text-muted-foreground">· {i.date || "sin fecha"}</span></span>
                        <span className="tabular">{formatMoney(i.amount || 0)}</span>
                    </div>
                ))}
                {items.length === 0 && <div className="overline text-muted-foreground">Sin registros este año</div>}
            </div>
        </div>
    );
}
